/**
 * LabOrderPrint — A4 print page for a lab order + its results.
 * Route: /lab/print/:id
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { PrintShell, PrintField, PrintSection, PrintSignatures, fmtDate } from '../components/print/PrintShell';

const LabOrderPrint = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/lab/orders')
      .then(res => {
        if (!mounted) return;
        const found = (res.data || []).find(o => o.id === id);
        setOrder(found || null);
      })
      .catch(() => { if (mounted) setOrder(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  const o = order || {};
  const status = o.status || 'pending';
  const statusColor = status === 'completed' ? '#10b981' : status === 'in_progress' ? '#f59e0b' : '#64748b';
  const tests = Array.isArray(o.tests) ? o.tests : [];
  const results = o.results || {};

  return (
    <PrintShell
      title="Lab Investigation Report"
      subtitle={status === 'completed' ? 'Final laboratory investigation report.' : 'Lab order requisition / interim report.'}
      loading={loading}
      notFound={!loading && !order}
      hospitalId={o.hospital_id}
      docRef={o.order_no || `LAB-${(o.id || '').slice(0, 8).toUpperCase()}`}
      statusPill={{ label: status.replace('_', ' '), color: statusColor }}>
      <PrintSection title="Patient Details">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <PrintField label="Patient Name" value={o.patient_name} />
          <PrintField label="Order Number" value={o.order_no} mono />
          <PrintField label="Ordered By" value={o.ordered_by_name} />
          <PrintField label="Ordered On" value={fmtDate(o.created_at)} />
        </div>
      </PrintSection>

      <PrintSection title={status === 'completed' ? 'Investigation Results' : 'Tests Ordered'}>
        <table className="w-full text-xs border border-slate-200 rounded overflow-hidden">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-1.5 text-left">Investigation</th>
              <th className="px-2 py-1.5 text-left">Result</th>
              <th className="px-2 py-1.5 text-left">Unit</th>
              <th className="px-2 py-1.5 text-left">Reference Range</th>
              <th className="px-2 py-1.5 text-left">Flag</th>
            </tr>
          </thead>
          <tbody>
            {tests.length === 0 ? (
              <tr><td colSpan={5} className="px-2 py-3 text-center text-slate-400">No tests on this order.</td></tr>
            ) : tests.map((t) => {
              const r = results[t.id] || {};
              const flagColor = r.flag === 'critical' ? '#dc2626'
                : r.flag === 'high' ? '#ea580c'
                : r.flag === 'low' ? '#2563eb'
                : r.flag === 'normal' ? '#059669'
                : '#64748b';
              return (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 font-medium">{t.name}</td>
                  <td className="px-2 py-1.5 font-mono font-bold" style={{ color: r.flag ? flagColor : undefined }}>{r.value || '—'}</td>
                  <td className="px-2 py-1.5">{r.unit || t.unit || '—'}</td>
                  <td className="px-2 py-1.5 text-[10px]">{t.reference_range || '—'}</td>
                  <td className="px-2 py-1.5">
                    {r.flag ? (
                      <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded" style={{ background: `${flagColor}22`, color: flagColor }}>{r.flag}</span>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </PrintSection>

      {Object.values(results).some(r => r?.notes) && (
        <PrintSection title="Clinical Notes">
          <ul className="text-xs text-slate-700 list-disc pl-5 space-y-0.5">
            {tests.map(t => results[t.id]?.notes ? <li key={t.id}><strong>{t.name}:</strong> {results[t.id].notes}</li> : null)}
          </ul>
        </PrintSection>
      )}

      <PrintSignatures
        left="Lab Technician"
        leftLabel={o.completed_by_name || '—'}
        right="Pathologist / Verified By"
        rightLabel={o.verified_by_name || '—'}
      />
    </PrintShell>
  );
};

export default LabOrderPrint;
